import React from 'react';
import { Building2, Mail, Phone, Star } from 'lucide-react';

interface Stakeholder {
  id: string;
  name: string;
  role: string;
  organization: string;
  type: string;
  email: string;
  phone: string;
  influence: 'high' | 'medium' | 'low';
  support: 'positive' | 'neutral' | 'negative';
}

interface StakeholderListProps {
  dealId?: string;
  searchTerm: string;
  filterType: string;
}

export default function StakeholderList({ dealId, searchTerm, filterType }: StakeholderListProps) {
  // Sample stakeholders - in a real app, this would come from a database
  const stakeholders: Stakeholder[] = [
    {
      id: '1',
      name: 'John Smith',
      role: 'CEO',
      organization: 'Target Company',
      type: 'external',
      email: 'john@example.com',
      phone: '+1 234 567 8900',
      influence: 'high',
      support: 'positive'
    },
    // Add more sample stakeholders as needed
  ];

  const getInfluenceColor = (influence: string) => {
    switch (influence) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const getSupportColor = (support: string) => {
    switch (support) {
      case 'positive':
        return 'bg-green-100 text-green-800';
      case 'negative':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="divide-y divide-gray-200">
      {stakeholders.map((stakeholder) => (
        <div key={stakeholder.id} className="p-6 hover:bg-gray-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-lg font-medium text-blue-800">
                  {stakeholder.name[0]}
                </span>
              </div>
              
              <div>
                <h4 className="text-lg font-medium text-gray-900">{stakeholder.name}</h4>
                <p className="text-sm text-gray-500">{stakeholder.role}</p>
                
                <div className="mt-2 flex items-center space-x-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <Building2 className="w-4 h-4 mr-1" />
                    {stakeholder.organization}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Mail className="w-4 h-4 mr-1" />
                    {stakeholder.email}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Phone className="w-4 h-4 mr-1" />
                    {stakeholder.phone}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getInfluenceColor(stakeholder.influence)}`}>
                {stakeholder.influence.toUpperCase()} Influence
              </span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSupportColor(stakeholder.support)}`}>
                {stakeholder.support.toUpperCase()} Support
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}